// RFC-0944 — user-rules body, extracted from UserRulesModal so it can render
// BOTH inside the legacy standalone modal and as a section of the unified
// settings shell without duplicating the behaviour that actually matters here:
// fetching, loading/error states, dirty tracking, the unsaved-changes guard,
// NAD logging, and discard-dialog focus. `RulesEditor` alone provides none of
// that — it is only the form.
//
// The component owns no chrome. Its host supplies the dialog, and drives the
// guard through the `SectionController` handle (see sectionController.ts): the
// host `await`s `requestTransition(target)` and only navigates on `true`. That
// ordering is what keeps the draft alive while the confirmation is on screen.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

import Modal from '../ui/Modal'
import RulesEditor from './RulesEditor'
import { useUserRules } from '../../hooks/useUserRules'
import { createNadDebug } from '../../utils/debugLog'
import type { SectionController, SectionTransitionTarget } from '../settings/sectionController'
import { trackInteraction } from '../../analytics/interactionAnalytics'

const log = createNadDebug('dw:rules-modal')

export interface UserRulesContentProps {
  /** Whether the section is on screen. Drives fetch + state reset (was `isOpen`). */
  active: boolean
  /** Origin surface, for NAD trace parity (landing vs dashboard). */
  surface?: 'dashboard' | 'landing'
}

function UserRulesContentImpl(
  { active, surface = 'dashboard' }: UserRulesContentProps,
  ref: React.Ref<SectionController>,
): JSX.Element {
  const { t } = useTranslation('pages')
  const { doc, version, history, loading, loadError, save } = useUserRules(active)
  const [dirty, setDirty] = useState(false)
  const [discardPrompt, setDiscardPrompt] = useState(false)
  const discardCancelRef = useRef<HTMLButtonElement>(null)

  // Resolver for the in-flight requestTransition promise. Held in a ref because
  // the confirm/cancel handlers settle it from a later render.
  const pendingDecisionRef = useRef<((allow: boolean) => void) | null>(null)
  const pendingTargetRef = useRef<SectionTransitionTarget | null>(null)

  const settle = useCallback((allow: boolean) => {
    const resolve = pendingDecisionRef.current
    pendingDecisionRef.current = null
    pendingTargetRef.current = null
    setDiscardPrompt(false)
    resolve?.(allow)
  }, [])

  useEffect(() => {
    if (!active) return
    setDirty(false)
    setDiscardPrompt(false)
    log({
      event: 'modal:open',
      trace_id: `rules-modal:${surface}`,
      surface,
      modal: 'user-rules',
    })
  }, [active, surface])

  // Never leave a caller awaiting a promise that can no longer settle: if the
  // section is unmounted or deactivated mid-decision, treat it as "allow" —
  // the host has already moved on.
  useEffect(() => {
    if (active) return
    if (pendingDecisionRef.current) settle(true)
  }, [active, settle])
  useEffect(() => () => { pendingDecisionRef.current?.(true) }, [])

  useImperativeHandle(
    ref,
    (): SectionController => ({
      isDirty: dirty,
      requestTransition: (target) => {
        if (!dirty) {
          log({
            event: 'modal:close',
            trace_id: `rules-modal:${surface}`,
            surface,
            modal: 'user-rules',
            dirty: false,
            target,
          })
          return Promise.resolve(true)
        }
        // Collapse a second request while one is already on screen rather than
        // orphaning the first resolver.
        if (pendingDecisionRef.current) return Promise.resolve(false)
        pendingTargetRef.current = target
        setDiscardPrompt(true)
        return new Promise<boolean>((resolve) => {
          pendingDecisionRef.current = resolve
        })
      },
    }),
    [dirty, surface],
  )

  const confirmDiscard = useCallback(() => {
    trackInteraction('settings_rules_action', { action: 'discard', scope: 'user' })
    log({
      event: 'modal:close',
      trace_id: `rules-modal:${surface}`,
      surface,
      modal: 'user-rules',
      dirty: true,
      target: pendingTargetRef.current,
    })
    settle(true)
  }, [settle, surface])

  const cancelDiscard = useCallback(() => settle(false), [settle])

  // A11y: when the discard prompt opens, move focus to its safe default action
  // ("Keep editing") so keyboard users land on a non-destructive control and
  // can Tab to "Discard changes". The shared Modal doesn't auto-focus content.
  useEffect(() => {
    if (discardPrompt) discardCancelRef.current?.focus()
  }, [discardPrompt])

  return (
    <>
      <div data-testid="user-rules-modal">
        {loading ? (
          <div
            className="flex items-center justify-center py-12 text-sm text-muted-foreground"
            data-testid="user-rules-modal-loading"
          >
            {t('rules.loading')}
          </div>
        ) : loadError !== null ? (
          <div
            className="py-8 text-center text-sm text-red-400"
            data-testid="user-rules-modal-error"
          >
            {t('rules.loadFailed', { message: loadError })}
          </div>
        ) : (
          <RulesEditor
            scope="user"
            initialDocument={doc}
            initialVersion={version}
            history={history}
            onSave={save}
            autoFocusBody
            onDirtyChange={setDirty}
          />
        )}
      </div>

      <Modal
        isOpen={active && discardPrompt}
        onClose={cancelDiscard}
        title={t('rules.discardTitle')}
        size="sm"
      >
        <div data-testid="user-rules-discard-dialog" className="space-y-4">
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <p className="min-w-0 text-sm text-foreground">{t('rules.discardConfirm')}</p>
          </div>

          <div className="flex justify-end gap-2">
            <button
              ref={discardCancelRef}
              type="button"
              data-testid="user-rules-discard-cancel"
              onClick={cancelDiscard}
              className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
            >
              {t('rules.discardKeepEditing')}
            </button>
            <button
              type="button"
              data-testid="user-rules-discard-confirm"
              onClick={confirmDiscard}
              className="h-9 rounded-lg bg-destructive px-3 text-sm font-medium text-white hover:bg-destructive/90"
            >
              {t('rules.discardConfirmCta')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

const UserRulesContent = forwardRef(UserRulesContentImpl)
UserRulesContent.displayName = 'UserRulesContent'
export default UserRulesContent
